import React, { useState, useEffect } from 'react';
import { pickingTasksAPI, PickingTask, PickingTaskItem } from '../../services/api';
import {
  CubeIcon,
  MapPinIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  ArrowLeftIcon,
  PlayIcon,
  QrCodeIcon,
  CheckIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

interface GuidedPickingInterfaceProps {
  taskId: number;
  onBack: () => void;
  onComplete: () => void;
}

export const GuidedPickingInterface: React.FC<GuidedPickingInterfaceProps> = ({ taskId, onBack, onComplete }) => {
  const [task, setTask] = useState<PickingTask | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentItemIndex, setCurrentItemIndex] = useState(0);
  const [scanInput, setScanInput] = useState('');
  const [confirmingItem, setConfirmingItem] = useState<number | null>(null);

  useEffect(() => {
    loadTask();
  }, [taskId]);

  const loadTask = async () => {
    try {
      setLoading(true);
      const data = await pickingTasksAPI.getById(taskId);
      setTask(data);
      // Find first unpicked item
      const firstUnpicked = data.items.findIndex(i => i.pickedQuantity < i.requiredQuantity);
      setCurrentItemIndex(firstUnpicked >= 0 ? firstUnpicked : 0);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to load task');
    } finally {
      setLoading(false);
    }
  };

  const handleStartPicking = async () => {
    if (!task) return;
    try {
      const updated = await pickingTasksAPI.startPicking(task.id);
      setTask(updated);
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleConfirmItem = async (item: PickingTaskItem, quantity: number, scannedCode?: string) => {
    if (!task) return;
    try {
      setConfirmingItem(item.id);
      setError('');

      // Validate quantity
      const qty = parseInt(String(quantity), 10);
      if (isNaN(qty) || qty <= 0) {
        throw new Error('Invalid quantity. Please enter a valid number.');
      }

      const updated = await pickingTasksAPI.confirmItemPicked(task.id, item.id, {
        pickedQuantity: qty,
        scannedBatchCode: scannedCode,
      });
      setTask(updated);
      setScanInput('');
      // Move to next unpicked item
      const nextUnpicked = updated.items.findIndex((i, idx) => idx > currentItemIndex && i.pickedQuantity < i.requiredQuantity);
      if (nextUnpicked >= 0) {
        setCurrentItemIndex(nextUnpicked);
      }
    } catch (err: any) {
      const errorMsg = err.response?.data?.message || err.message || 'Failed to confirm item picked';
      setError(errorMsg);
      console.error('Error confirming item:', err);
    } finally {
      setConfirmingItem(null);
    }
  };

  const handleCompletePicking = async () => {
    if (!task) return;
    try {
      await pickingTasksAPI.completePicking(task.id);
      onComplete();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const getProgress = () => {
    if (!task?.items.length) return { picked: 0, total: 0, percent: 0 };
    const picked = task.items.reduce((sum, i) => sum + i.pickedQuantity, 0);
    const total = task.items.reduce((sum, i) => sum + i.requiredQuantity, 0);
    return { picked, total, percent: total > 0 ? Math.round((picked / total) * 100) : 0 };
  };

  const allItemsPicked = task?.items.every(i => i.pickedQuantity >= i.requiredQuantity);

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" /></div>;
  }

  if (!task) {
    return <div className="text-center py-12 text-red-600"><ExclamationCircleIcon className="w-12 h-12 mx-auto mb-4" /><p>Task not found</p></div>;
  }

  const progress = getProgress();
  const currentItem = task.items[currentItemIndex];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <button onClick={onBack} className="flex items-center gap-2 text-gray-600 hover:text-gray-900">
          <ArrowLeftIcon className="w-5 h-5" /> Back to List
        </button>
        <div className="text-right">
          <div className="text-sm text-gray-500">Task #{task.id}</div>
          <div className="font-semibold">{task.deal?.orderNumber || `Deal #${task.dealId}`}</div>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="bg-white rounded-lg p-4 shadow-sm border">
        <div className="flex items-center justify-between mb-2">
          <span className="font-medium">Picking Progress</span>
          <span className="text-sm text-gray-600">{progress.picked} / {progress.total} items ({progress.percent}%)</span>
        </div>
        <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
          <div className="h-full bg-blue-600 rounded-full transition-all" style={{ width: `${progress.percent}%` }} />
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          <ExclamationCircleIcon className="w-5 h-5 inline mr-2" />{error}
          <button onClick={() => setError(null)} className="ml-4 text-sm underline">Dismiss</button>
        </div>
      )}

      {/* Start Picking Button */}
      {task.status === 'TO_PICK' && (
        <div className="text-center py-8">
          <button onClick={handleStartPicking} className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 mx-auto">
            <PlayIcon className="w-5 h-5" /> Start Picking
          </button>
        </div>
      )}

      {/* Current Item to Pick */}
      {task.status === 'IN_PROGRESS' && currentItem && (
        <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
          <div className="bg-blue-600 text-white px-4 py-3 flex items-center justify-between">
            <span className="font-medium">Current Item ({currentItemIndex + 1} of {task.items.length})</span>
            <span className="text-sm opacity-80">Route Order: {currentItem.routeOrder}</span>
          </div>
          <div className="p-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-xl font-semibold mb-2">{currentItem.productName}</h3>
                {currentItem.sku && <p className="text-gray-600 mb-4">SKU: {currentItem.sku}</p>}
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2 text-gray-600">
                    <MapPinIcon className="w-4 h-4" />
                    <span className="font-medium">Location:</span>
                    <span>{[currentItem.warehouseCode, currentItem.shelfCode, currentItem.binCode, currentItem.trayCode].filter(Boolean).join(' → ') || 'Not specified'}</span>
                  </div>
                  {currentItem.batchNumber && (
                    <div className="flex items-center gap-2 text-gray-600">
                      <CubeIcon className="w-4 h-4" />
                      <span className="font-medium">Batch/LOT:</span>
                      <span className="font-mono bg-yellow-100 px-2 py-0.5 rounded">{currentItem.batchNumber}</span>
                    </div>
                  )}
                </div>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="text-center mb-4">
                  <div className="text-4xl font-bold text-blue-600">{currentItem.requiredQuantity}</div>
                  <div className="text-gray-500">Required Quantity</div>
                </div>
                {currentItem.batchNumber && (
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Scan Batch Code</label>
                    <div className="flex gap-2">
                      <input type="text" value={scanInput} onChange={(e) => setScanInput(e.target.value)} placeholder="Scan or enter batch code" className="flex-1 px-3 py-2 border rounded-lg" />
                      <button className="p-2 bg-gray-200 rounded-lg"><QrCodeIcon className="w-5 h-5" /></button>
                    </div>
                  </div>
                )}
                <button
                  onClick={() => handleConfirmItem(currentItem, currentItem.requiredQuantity, scanInput || undefined)}
                  disabled={confirmingItem === currentItem.id}
                  className="w-full py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {confirmingItem === currentItem.id ? 'Confirming...' : <><CheckIcon className="w-5 h-5" /> Confirm Picked</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Complete Picking Button */}
      {task.status === 'IN_PROGRESS' && allItemsPicked && (
        <div className="text-center">
          <button onClick={handleCompletePicking} className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2 mx-auto">
            <CheckCircleIcon className="w-5 h-5" /> Complete Picking & Proceed to QC
          </button>
        </div>
      )}

      {/* Items List */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="px-4 py-3 border-b font-medium">All Items</div>
        <div className="divide-y">
          {task.items.map((item, idx) => (
            <div key={item.id} onClick={() => task.status === 'IN_PROGRESS' && setCurrentItemIndex(idx)}
              className={`p-4 flex items-center justify-between cursor-pointer hover:bg-gray-50 ${idx === currentItemIndex ? 'bg-blue-50' : ''}`}>
              <div className="flex items-center gap-3">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${item.pickedQuantity >= item.requiredQuantity ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'}`}>
                  {item.pickedQuantity >= item.requiredQuantity ? <CheckIcon className="w-5 h-5" /> : item.routeOrder}
                </div>
                <div>
                  <div className="font-medium">{item.productName}</div>
                  <div className="text-sm text-gray-500">{[item.warehouseCode, item.shelfCode, item.binCode, item.trayCode].filter(Boolean).join(' → ')}</div>
                </div>
              </div>
              <div className="text-right">
                <div className="font-medium">{item.pickedQuantity} / {item.requiredQuantity}</div>
                {item.batchNumber && <div className="text-xs text-gray-500">Batch: {item.batchNumber}</div>}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default GuidedPickingInterface;

